import React, { useRef } from 'react';

import PropTypes from 'prop-types';
import { Transition } from 'react-transition-group';
import styled, { css } from 'styled-components';

import { useToastContext } from './ToastContext';

const transitionIn = css`
  transform: translate3d(0, 0, 0);
`;

const transitionOut = css`
  transform: translate3d(0, -100%, 0);
`;

const Wrapper = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: ${(props) => (props.transitionState === 'exited' ? 0 : 'auto')};
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
  overflow: hidden;
  z-index: ${(props) => (props.hideBehind ? -1 : 6)};
`;

const Message = styled.div`
  background-color: ${({ theme, isError }) =>
    isError ? theme.colors.toastErrorMessage : theme.colors.toastNormalMessage};
  color: ${({ theme }) => theme.colors.toastMessageTextColor};
  ${(props) =>
    props.transitionState === 'exited' || props.transitionState === 'exiting'
      ? transitionOut
      : transitionIn};
  transition-property: transform;
  transition-duration: 300ms;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px;
  justify-content: center;
`;

// This isn't currently ideal because there is a bug in react-transition-group:
// https://github.com/reactjs/react-transition-group/issues/338
const ToastMessages = ({ className, 'data-autotag': dataAutoTag }) => {
  const lastMessageRef = useRef(null);
  const {
    toastMessage,
    hideMessage,
    persist,
    isError = false,
  } = useToastContext();

  return (
    <Transition in={!!toastMessage} timeout={300}>
      {(state) => {
        // On transition out, keep showing the last message, otherwise it
        // will disappear instead of transitioning.
        if (toastMessage) {
          lastMessageRef.current = toastMessage;
        }
        return (
          <Wrapper
            className={className}
            transitionState={state}
            aria-hidden={toastMessage ? 'false' : 'true'}
          >
            <Message
              data-autotag={dataAutoTag}
              transitionState={state}
              isError={isError}
              onClick={persist ? null : hideMessage}
            >
              {toastMessage || lastMessageRef.current}
            </Message>
          </Wrapper>
        );
      }}
    </Transition>
  );
};

ToastMessages.propTypes = {
  className: PropTypes.string,
  'data-autotag': PropTypes.string,
};

ToastMessages.Message = Message;

export default ToastMessages;
